import * as yup from 'yup';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TFunction } from 'i18next';
import { isEmail, INVALID_EMAIL } from 'utils/validation-rules';
import { ProfileStep } from './steps';

dayjs.extend(customParseFormat);

/** Formats accepted in the Date of birth text input (the API stores YYYY-MM-DD). */
const DOB_INPUT_FORMATS = ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'];

/** Strictly parse a typed date of birth; returns an invalid dayjs for bad input. */
export const parseDobInput = (value: string) =>
  dayjs(value.trim().replace(/\s/g, ''), DOB_INPUT_FORMATS, true);

/**
 * Deliberately trivial client-side checks — only a parseable date of birth
 * (required for the YYYY-MM-DD conversion on submit) and email shapes for
 * the fields the backend format-checks (@IsEmail → 422). Everything else is
 * left to the backend (its 422s surface via the error modal); the banking
 * proof is the real verification.
 */
const emailShape = (t: TFunction) =>
  yup
    .string()
    .trim()
    .matches(isEmail, {
      message: t(INVALID_EMAIL),
      excludeEmptyString: true,
    });

const AboutSchema = (t: TFunction) =>
  yup.object({
    dateOfBirth: yup
      .string()
      .test('valid-date', t('Please enter a valid date of birth'), (value) => {
        if (!value || !value.trim()) return true;
        const date = parseDobInput(value);
        return date.isValid() && date.year() >= 1900 && !date.isAfter(dayjs(), 'day');
      }),
  });

export const getStepSchema = (step: ProfileStep, t: TFunction) => {
  switch (step.id) {
    case 'about':
      return AboutSchema(t);
    case 'contact':
      return yup.object({ secondaryEmail: emailShape(t) });
    case 'advance':
      return yup.object({ recoupmentEmail: emailShape(t) });
    case 'marketing':
      return yup.object({ contactEmail: emailShape(t) });
    default:
      return yup.object({});
  }
};

/**
 * PATCH field-level 422s arrive in the class-validator shape:
 * `message: [{ property, constraints: { <code>: <English fallback> } }]`.
 * The constraint key is a stable code translated here; the backend's
 * fallback text is used only for codes we don't know.
 */
const CONSTRAINT_MESSAGES: Record<string, string> = {
  requiredOnCompleted: 'Field is required',
  validDate: 'Please enter a valid date of birth',
  isEmail: INVALID_EMAIL,
  isIn: 'Please select one of the offered options',
  invalidCountry: 'Please select a country from the list',
  invalidState: 'Please select a state from the list',
  matches: 'Please enter a valid CPF (000.000.000-00) or CNPJ (00.000.000/0000-00)',
};

export interface ServerFieldError {
  field: string;
  message: string;
}

/** Extract per-field errors from a 422 response; null when it's not that shape. */
export const getServerFieldErrors = (err: any, t: TFunction): ServerFieldError[] | null => {
  const message = err?.data?.message;
  if (!Array.isArray(message)) return null;
  const errors: ServerFieldError[] = [];
  message.forEach((entry) => {
    if (typeof entry?.property !== 'string' || !entry?.constraints) return;
    const codes = Object.keys(entry.constraints);
    const known = codes.find((code) => CONSTRAINT_MESSAGES[code]);
    const text = known ? t(CONSTRAINT_MESSAGES[known]) : entry.constraints[codes[0]];
    if (text) errors.push({ field: entry.property, message: text });
  });
  return errors;
};
